/** Client cancellation identity only. Supabase and the server still authenticate every request. */
export interface MirrorAuthIdentity {
  readonly ownerId: string;
  readonly authSessionId: string;
  readonly accessToken: string;
}
export interface MirrorAuthLease extends MirrorAuthIdentity { readonly generation: number }
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu;
const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
function payload(token: unknown): Record<string, unknown> | null {
  if (typeof token !== 'string' || token.length > 16384) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const value: unknown = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    return object(value) ? value : null;
  } catch { return null; }
}
/** Preserve the legacy SID-only parser contract; this is never an authentication grant. */
export function mirrorAuthSessionId(token: unknown): string | null {
  const claim = payload(token);
  return claim && typeof claim.session_id === 'string' && uuid.test(claim.session_id)
    ? claim.session_id.toLowerCase() : null;
}
export function mirrorAuthIdentity(session: unknown): MirrorAuthIdentity | null {
  if (!object(session) || !object(session.user) || typeof session.user.id !== 'string'
    || !uuid.test(session.user.id) || typeof session.access_token !== 'string') return null;
  const parts = session.access_token.split('.');
  if (parts.length !== 3 || parts.some(part => !/^[A-Za-z0-9_-]+$/u.test(part))) return null;
  const claim = payload(session.access_token), authSessionId = mirrorAuthSessionId(session.access_token);
  if (!claim || !authSessionId || claim.sub !== session.user.id) return null;
  return Object.freeze({ ownerId: session.user.id, authSessionId, accessToken: session.access_token });
}
export function sameMirrorAuthIdentity(a: MirrorAuthIdentity | null, b: MirrorAuthIdentity | null): boolean {
  return a === null || b === null ? a === b : a.ownerId === b.ownerId && a.authSessionId === b.authSessionId;
}
export interface MirrorAuthSessionScope {
  current(): MirrorAuthIdentity | null;
  capture(): MirrorAuthLease | null;
  isCurrent(lease: MirrorAuthLease | null): boolean;
  matchesSession(session: unknown, lease: MirrorAuthLease | null): boolean;
  observeSession(session: unknown): { identity: MirrorAuthIdentity | null; changed: boolean };
  revision(): number;
  invalidate(): void;
}
/** Synchronous observations revoke leases, including A -> B -> A. Reads cannot adopt an actor. */
export function createMirrorAuthSessionScope(): MirrorAuthSessionScope {
  let identity: MirrorAuthIdentity | null = null, generation = 0, revision = 0;
  const isCurrent = (lease: MirrorAuthLease | null) =>
    !!lease && lease.generation === generation && sameMirrorAuthIdentity(identity, lease);
  return {
    current: () => identity,
    capture: () => identity ? Object.freeze({ ...identity, generation }) : null,
    isCurrent,
    matchesSession: (session, lease) => isCurrent(lease) && sameMirrorAuthIdentity(mirrorAuthIdentity(session), lease),
    observeSession(session) {
      revision++;
      const next = mirrorAuthIdentity(session), changed = !sameMirrorAuthIdentity(identity, next);
      if (changed) generation++;
      identity = next;
      return { identity, changed };
    },
    revision: () => revision,
    invalidate() { revision++; generation++; identity = null; },
  };
}
/** Generic transport callbacks may be composed with an identity authority. */
export interface MirrorChannelTransportState {
  ownerId: string | null;
  authEpoch: number;
  sessionEpoch: number;
  sessionTransition: boolean;
  request: number;
  mounted: boolean;
  controller: AbortController | null;
  authScope?: MirrorAuthSessionScope;
}
/** Every production MirrorChat instance requires the strict cancellation authority. */
export interface AuthenticatedMirrorChannelState extends MirrorChannelTransportState {
  authScope: MirrorAuthSessionScope;
}
